import React, { useEffect, useMemo, useState } from "react";
import InstituteSidebar from "./Institutesidebar";
import InstituteTopbar from "./Institutetopbar";
import {
  getInstituteSchedulingRegistrations,
  bulkScheduleInstituteRegistrations,
} from "../api/instituteclient";

const listFrom = (data) =>
  Array.isArray(data) ? data : data?.results || [];

export default function InstituteScheduling() {
  const [registrations, setRegistrations] = useState([]);
  const [selected, setSelected] = useState([]);
  const [examId, setExamId] = useState("");
  const [examDate, setExamDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getInstituteSchedulingRegistrations();
      const rows = listFrom(response.data);

      setRegistrations(rows);
      setSelected((current) =>
        current.filter((id) => rows.some((row) => row.id === id))
      );
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Unable to load approved registrations."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const exams = useMemo(() => {
    const map = new Map();

    registrations.forEach((registration) => {
      map.set(
        String(registration.exam_id),
        registration.exam_name
      );
    });

    return [...map.entries()];
  }, [registrations]);

  const filtered = useMemo(
    () =>
      registrations.filter(
        (registration) =>
          !examId ||
          String(registration.exam_id) === String(examId)
      ),
    [registrations, examId]
  );

  const toggle = (id) => {
    setSelected((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  };

  const selectAll = () => {
    const ids = filtered.map((registration) => registration.id);

    setSelected((current) =>
      ids.every((id) => current.includes(id))
        ? current.filter((id) => !ids.includes(id))
        : [...new Set([...current, ...ids])]
    );
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!selected.length || !examId || !examDate || !startTime) {
      setError(
        "Choose an exam, select at least one student, and enter the date and start time."
      );
      return;
    }

    const selectedRows = registrations.filter((registration) =>
      selected.includes(registration.id)
    );

    if (
      selectedRows.some(
        (registration) =>
          String(registration.exam_id) !== String(examId)
      )
    ) {
      setError("Select registrations for the same exam only.");
      return;
    }

    setSubmitting(true);

    try {
      const response = await bulkScheduleInstituteRegistrations({
        registration_ids: selected,
        exam_id: Number(examId),
        exam_date: examDate,
        start_time: startTime,
      });

      setMessage(
        response.data?.message ||
          "Scheduling completed. Check email delivery for OTPs."
      );

      setSelected([]);
      await load();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Scheduling failed. Refresh the registrations and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-page-full d-flex">
      <InstituteSidebar />

      <div className="admin-content">
        <InstituteTopbar />

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <h2 className="h5 fw-bold mb-1">Scheduling</h2>
            <p className="text-muted small mb-0">
              Schedule registrations with approved institute payments.
            </p>
          </div>

          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={load}
            disabled={loading || submitting}
          >
            Refresh
          </button>
        </div>

        {error && (
          <div className="alert alert-danger py-2">{error}</div>
        )}

        {message && (
          <div className="alert alert-success py-2">{message}</div>
        )}

        <form onSubmit={submit}>
          <div className="admin-card p-3 mb-3">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-4">
                <label className="form-label">Exam</label>

                <select
                  className="form-select"
                  value={examId}
                  onChange={(event) => {
                    setExamId(event.target.value);
                    setSelected([]);
                  }}
                  required
                >
                  <option value="">Select exam</option>

                  {exams.map(([id, name]) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-6 col-md-3">
                <label className="form-label">Exam date</label>

                <input
                  className="form-control"
                  type="date"
                  min={new Date().toLocaleDateString("en-CA")}
                  value={examDate}
                  onChange={(event) => setExamDate(event.target.value)}
                  required
                />
              </div>

              <div className="col-6 col-md-3">
                <label className="form-label">Start time</label>

                <input
                  className="form-control"
                  type="time"
                  value={startTime}
                  onChange={(event) => setStartTime(event.target.value)}
                  required
                />
              </div>

              <div className="col-12 col-md-2">
                <button
                  className="btn btn-primary w-100"
                  type="submit"
                  disabled={submitting || loading}
                >
                  {submitting
                    ? "Scheduling..."
                    : `Schedule (${selected.length})`}
                </button>
              </div>
            </div>
          </div>

          <div className="admin-card p-3 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="h6 fw-bold mb-0">
                Approved registrations
              </h3>

              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={selectAll}
                disabled={!filtered.length || loading}
              >
                Select / clear all
              </button>
            </div>

            {loading ? (
              <p className="text-muted mb-0">
                Loading approved registrations...
              </p>
            ) : filtered.length === 0 ? (
              <p className="text-muted mb-0">
                No approved, unscheduled registrations are available
                for this exam.
              </p>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle admin-table mb-0">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Student</th>
                      <th>Email</th>
                      <th>Exam</th>
                      <th>Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filtered.map((registration) => (
                      <tr key={registration.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.includes(registration.id)}
                            onChange={() => toggle(registration.id)}
                            aria-label={`Select ${registration.student_name}`}
                          />
                        </td>

                        <td className="fw-semibold">
                          {registration.student_name}
                        </td>

                        <td>{registration.student_email}</td>
                        <td>{registration.exam_name}</td>

                        <td>
                          <span className="admin-status-pill active">
                            {registration.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-muted small mt-3 mb-0">
              Each scheduled student receives an individual verification
              code by email.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}